import React, { useEffect, useMemo, useState } from 'react';
import { Button, DataGrid, Select, Typography } from '../../design-system';
import {
  SwHeightParameterPair,
  SwHeightResult,
  SwHeightSample,
  SwHeightSettings,
  WellLogs
} from '../../@types/swHeight/types';
import { PARAMETER_LABELS } from './swHeightSettings';
import styles from './SwHeightTable.module.css';

export interface SwHeightTableProps {
  logs: WellLogs;
  result: SwHeightResult | null;
  settings: SwHeightSettings;
  samples?: SwHeightSample[];
}

export type DataRow = {
  index: number;
  depth: number | null;
  tvdss: number | null;
  phit: number | null;
  perm: number | null;
  measured: number | null;
  rqi: number | null;
  fzi: number | null;
  rt: number | null;
  hafwl: number | null;
  pc: number | null;
  swirr: number | null;
  pe: number | null;
  lambda: number | null;
  bestA: number | null;
  bestB: number | null;
  truePcMax: number | null;
  sw: number | null;
  swByRockType: number | null;
  hasIssue: boolean;
};

// DataGrid renders every row it is given, so long datasets are paged
// here rather than handed over whole.
const ROWS_PER_PAGE = 100;

// The table describes the calibrated log dataset, so the measured column is
// always the log SWT. Core lab Sw is a chart overlay, not a table column.
export const MEASURED_HEADER = 'SWT';

const formatValue = (
  value: number | null | undefined,
  decimals = 4
): string => {
  if (value === null || value === undefined) return '—';
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(decimals);
};

const formatPair = (pair: SwHeightParameterPair | null): string => {
  if (!pair) return '—';
  const source = pair.source === 'manual' ? 'manual' : 'fit';
  // Name the coefficients for the fitted form; "P1"/"P2" say nothing, and
  // "Intercept"/"Slope" would be wrong for power and exponential.
  const labels = PARAMETER_LABELS[pair.equation_type];
  return (
    `${labels.p1} = ${pair.p1}, ${labels.p2} = ${pair.p2} ` +
    `(${pair.equation_type}, ${source})`
  );
};

const isInvalid = (value: number | null) =>
  value === null || value === undefined || !Number.isFinite(value);

/*
 * Every numeric column gets the same explicit width, and the grid gets a
 * `minWidth` of their sum.
 *
 * Both halves are needed. DataGrid's table is `width: 100%; table-layout:
 * fixed`, so without a width floor it can never exceed its container: fifteen
 * unsized columns get squeezed to a share each, and `.th { white-space: nowrap }`
 * then spills every cell across its neighbour -- which is how a depth and a
 * porosity ended up rendered on top of each other. With the floor in place the
 * grid's own `overflow-x: auto` takes over and the columns keep their width.
 *
 * 96px holds the widest thing these columns render: a five-figure depth, or a
 * value at four decimals.
 */
const NUMBER_COLUMN_WIDTH = 96;

const numberColumn = (key: keyof DataRow, header: string) => ({
  key: key as string,
  header,
  width: `${NUMBER_COLUMN_WIDTH}px`,
  align: 'right' as const,
  getValue: (row: DataRow) => formatValue(row[key] as number | null)
});

/**
 * The calculated table: every imported input alongside every derived
 * column. Columns marked * depend on the calibration variables (FWL,
 * gradient difference, parameter pairs) shown above the grid.
 */
export const SwHeightTable: React.FC<SwHeightTableProps> = ({
  logs,
  result,
  settings,
  samples = []
}) => {
  const [page, setPage] = useState(0);
  const [issuesOnly, setIssuesOnly] = useState(false);

  const rows = useMemo<DataRow[]>(() => {
    const curves = logs.curves;
    const depth = curves.md ?? [];
    const phit = curves.phit ?? curves.phie ?? [];
    return depth.map((d, i) => {
      const phitValue = phit[i] ?? null;
      const permValue = curves.perm?.[i] ?? null;
      const tvdssValue = curves.tvdss?.[i] ?? null;
      const swValue = result?.sw?.[i] ?? null;
      return {
        index: i,
        depth: d,
        tvdss: tvdssValue,
        phit: phitValue,
        perm: permValue,
        measured: curves.swt?.[i] ?? null,
        rqi: result?.rqi?.[i] ?? null,
        fzi: result?.fzi?.[i] ?? null,
        rt: result?.rock_type?.[i] ?? null,
        hafwl: result?.hafwl?.[i] ?? null,
        pc: result?.pc?.[i] ?? null,
        swirr: result?.swirr?.[i] ?? null,
        pe: result?.pe?.[i] ?? null,
        lambda: result?.lambda_value?.[i] ?? null,
        bestA: result?.best_a?.[i] ?? null,
        bestB: result?.best_b?.[i] ?? null,
        truePcMax: result?.true_pc_max?.[i] ?? null,
        sw: swValue,
        swByRockType: result?.sw_by_rock_type?.[i] ?? null,
        hasIssue:
          isInvalid(swValue) ||
          isInvalid(phitValue) ||
          isInvalid(permValue) ||
          isInvalid(tvdssValue)
      };
    });
  }, [logs, result]);

  const filtered = useMemo(
    () => (issuesOnly ? rows.filter((row) => row.hasIssue) : rows),
    [rows, issuesOnly]
  );

  const pageCount = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));

  useEffect(() => {
    if (page > pageCount - 1) {
      setPage(0);
    }
  }, [page, pageCount]);

  const safePage = Math.min(page, pageCount - 1);
  const visible = useMemo(
    () =>
      filtered.slice(
        safePage * ROWS_PER_PAGE,
        safePage * ROWS_PER_PAGE + ROWS_PER_PAGE
      ),
    [filtered, safePage]
  );

  const issueCount = useMemo(
    () => rows.filter((row) => row.hasIssue).length,
    [rows]
  );

  const method = settings.method ?? 'brooks_corey';
  const isLiao = method === 'liao';

  const variables = useMemo(
    () => [
      { name: 'Free water level (TVDSS)', value: formatValue(settings.fwl) },
      {
        name: 'Gradient difference (psi/ft)',
        value: formatValue(settings.grad_diff)
      },
      { name: 'Method', value: isLiao ? 'Liao' : 'Brooks-Corey' },
      { name: 'Parameter variable', value: settings.x_axis.toUpperCase() },
      // These pairs drive Sw*; the rock-type table in the panel above
      // drives Sw (RT)*.
      { name: 'Swirr', value: formatPair(settings.swirr_params) },
      { name: 'Pe', value: formatPair(settings.pe_params) },
      ...(isLiao
        ? [
            { name: 'a', value: formatPair(settings.best_a_params) },
            { name: 'b', value: formatPair(settings.best_b_params) },
            {
              name: 'Pc max',
              value: formatPair(settings.true_pc_max_params)
            }
          ]
        : [{ name: 'Lambda', value: formatPair(settings.lambda_params) }])
    ],
    [settings, isLiao]
  );

  // Lambda is derived rather than fitted in Liao mode, so it stays in the
  // grid -- seeing the value the inverse actually used is the fastest way to
  // spot the sign problem the validation report warns about.
  const derivedColumns = useMemo(
    () =>
      isLiao
        ? [
            numberColumn('lambda', 'Lambda (derived)*'),
            numberColumn('bestA', 'a*'),
            numberColumn('bestB', 'b*'),
            numberColumn('truePcMax', 'Pc max*')
          ]
        : [numberColumn('lambda', 'Lambda*')],
    [isLiao]
  );

  // One list, so the `minWidth` below is derived from the columns actually
  // rendered rather than from a hardcoded count -- Liao swaps one Lambda column
  // for four, so the widest layout would otherwise be the most cramped.
  const calculatedColumns = useMemo(
    () => [
      numberColumn('depth', 'Depth (MD)'),
      numberColumn('tvdss', 'TVDSS'),
      numberColumn('phit', 'PHIT'),
      numberColumn('perm', 'PERM'),
      numberColumn('measured', MEASURED_HEADER),
      numberColumn('rqi', 'RQI'),
      numberColumn('fzi', 'FZI'),
      numberColumn('rt', 'RT'),
      numberColumn('hafwl', 'HAFWL*'),
      numberColumn('pc', 'Pc*'),
      numberColumn('swirr', 'Swirr*'),
      numberColumn('pe', 'Pe*'),
      ...derivedColumns,
      numberColumn('sw', 'Sw*'),
      numberColumn('swByRockType', 'Sw (RT)*')
    ],
    [derivedColumns]
  );

  return (
    <div className={styles.wrapper} data-testid="sw-height-table">
      <Typography.Text weight="semibold">
        Variables used in the calculated columns (*)
      </Typography.Text>
      <DataGrid
        columns={[
          {
            key: 'name',
            header: 'Variable',
            getValue: (row: { name: string; value: string }) => row.name
          },
          {
            key: 'value',
            header: 'Value',
            getValue: (row: { name: string; value: string }) => row.value
          }
        ]}
        data={variables}
        rowKey="name"
      />

      {samples.length > 0 && (
        <div className={styles.table}>
          <DataGrid
            title={`Project samples (${samples.length})`}
            columns={[
              {
                key: 'sample_code',
                header: 'Sample',
                getValue: (row: SwHeightSample) =>
                  row.sample_code ?? row.sample_uid
              },
              {
                key: 'depth',
                header: 'Depth (MD)',
                getValue: (row: SwHeightSample) => formatValue(row.depth)
              },
              {
                key: 'porosity',
                header: 'PHIT',
                getValue: (row: SwHeightSample) => formatValue(row.porosity)
              },
              {
                key: 'permeability',
                header: 'PERM',
                getValue: (row: SwHeightSample) =>
                  formatValue(row.permeability)
              },
              {
                key: 'rqi',
                header: 'RQI',
                getValue: (row: SwHeightSample) => formatValue(row.rqi)
              },
              {
                key: 'fzi',
                header: 'FZI',
                getValue: (row: SwHeightSample) => formatValue(row.fzi)
              },
              {
                key: 'rock_type',
                header: 'RT',
                getValue: (row: SwHeightSample) => formatValue(row.rock_type)
              }
            ]}
            data={samples}
            rowKey="sample_uid"
            minWidth={NUMBER_COLUMN_WIDTH * 7}
          />
        </div>
      )}

      <div className={styles.toolbar}>
        <Typography.Text weight="semibold">
          Calculated data ({filtered.length} rows)
        </Typography.Text>
        <div className={styles.toolbarActions}>
          <Button
            variant={issuesOnly ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              setIssuesOnly((current) => !current);
              setPage(0);
            }}
            disabled={issueCount === 0}
            data-testid="toggle-issues-only"
          >
            {issuesOnly
              ? 'Show all rows'
              : `Rows with problems (${issueCount})`}
          </Button>
          {pageCount > 1 && (
            <div className={styles.pager}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((current) => Math.max(0, current - 1))}
                disabled={safePage === 0}
                data-testid="table-prev-page"
              >
                Previous
              </Button>
              <Select
                options={Array.from({ length: pageCount }, (_, i) => ({
                  value: String(i),
                  label: `Page ${i + 1} of ${pageCount}`
                }))}
                value={String(safePage)}
                onChange={(value) => setPage(Number(value))}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  setPage((current) => Math.min(pageCount - 1, current + 1))
                }
                disabled={safePage >= pageCount - 1}
                data-testid="table-next-page"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className={styles.table}>
        <DataGrid
          columns={calculatedColumns}
          data={visible}
          rowKey="index"
          minWidth={NUMBER_COLUMN_WIDTH * calculatedColumns.length}
          maxHeight="calc(100vh - 420px)"
          striped
          emptyMessage="No rows to display."
        />
      </div>
    </div>
  );
};

export default SwHeightTable;
